import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = anonymous, object = user
  const [user, setUser] = useState(null);

  const fetchMe = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(async (email, password, mfa_code) => {
    try {
      const payload = { email, password };
      if (mfa_code) payload.mfa_code = mfa_code;
      const { data } = await api.post("/auth/login", payload);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      const detail = e.response?.data?.detail;
      const mfaRequired = typeof detail === "string" && detail.toLowerCase().includes("mfa code required");
      return { ok: false, error: formatApiError(detail) || e.message, mfa_required: mfaRequired };
    }
  }, []);

  const register = useCallback(async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Non-fatal: server unreachable — still clear local state.
      // eslint-disable-next-line no-console
      console.warn("Logout API call failed:", e?.message || e);
    }
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, login, register, logout, refresh: fetchMe }),
    [user, login, register, logout, fetchMe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
